import { Box, Flex } from '@withjoy/joykit';
import { ChevronBack, ChevronRight } from '@withjoy/joykit/icons';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PropContent } from '../../../../../propValues.types';
import { CarouselImageContainer } from './CarouselImageContainer';
import { CarouselVideoContainer } from './CarouselVideoContainer';
import { CarouselButton } from './CarouselButton.styles';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHistory, useTranslation } from '@shared/core';
import { usePrefersReducedMotion } from '@shared/hooks/usePrefersReducedMotion';

type ContentCarouselProps = {
  content: PropContent[];
  link?: string;
  label?: string;
  telemetryId?: string;
  autoRotatePeriodMs?: number;
  // Per-slide navigation for "list" carousels where each slide links somewhere different.
  // Index-aligned with `content`; falls back to the tile-level `link`/`telemetryId` when absent.
  slideNav?: ReadonlyArray<{ link?: string; telemetryId?: string }>;
  // Pause auto-rotation while the pointer/focus is over the tile, resume on leave.
  pauseOnHover?: boolean;
};

export type CarouselSlideBaseProps = {
  asset: PropContent;
  currentPosition: number;
  onClick?: (index: number) => void;
  index: number;
  // Cut instantly instead of animating the slide (used for wrap-around jumps and reduced motion).
  disableTransition?: boolean;
};

type CarouselArrowProps = Readonly<{
  direction: 'prev' | 'next';
  onClick: (event: React.MouseEvent) => void;
  visible: boolean;
}>;

// Prev/next navigation arrow. Mirrors the arrow on the template GalleryTileCarousel for consistency.
const CarouselArrow = ({ direction, onClick, visible }: CarouselArrowProps) => {
  const { tq } = useTranslation('stationery');

  return (
    <Box
      as="button"
      type="button"
      aria-label={direction === 'prev' ? tq`Previous slide` : tq`Next slide`}
      onClick={onClick}
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="absolute"
      top="50%"
      {...(direction === 'prev' ? { left: '8px' } : { right: '8px' })}
      zIndex={6}
      width="28px"
      height="28px"
      borderRadius="50%"
      backgroundColor="white"
      color="mono14"
      boxShadow="0px 4px 12px -2px rgba(44, 41, 37, 0.2), 0px 2px 6px -2px rgba(0,0,0,0.15)"
      style={{
        transform: 'translateY(-50%)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 150ms ease',
        pointerEvents: visible ? 'auto' : 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        lineHeight: 0,
      }}
    >
      {direction === 'prev' ? (
        <ChevronBack
          size={16}
          title={undefined}
          aria-hidden
        />
      ) : (
        <ChevronRight
          size={16}
          title={undefined}
          aria-hidden
        />
      )}
    </Box>
  );
};

const ContentCarousel = (props: ContentCarouselProps) => {
  const { content, telemetryId, link, label, autoRotatePeriodMs, slideNav, pauseOnHover } = props;
  const mobile = useIsMobileScreen();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [currentPage, setCurrentPage] = useState(0);
  const [hover, setHover] = useState(false);
  // A page change of more than one slot (wrap-around, or a non-adjacent dot click) would make every
  // slide sweep across the clipped tile at once, flashing it blank. For that single step we cut
  // instantly instead of animating; the transition is restored on the next commit.
  const [suppressTransition, setSuppressTransition] = useState(false);
  const currentPageRef = useRef(currentPage);
  const { eventHandle } = useEventInfo();
  const history = useHistory();

  const { valuePropLinkInteracted } = useCardTelemetry();

  const navigate = useCallback(
    (targetLink: string | undefined, targetTelemetryId: string | undefined, slide: number) => {
      if (!targetLink) return;

      valuePropLinkInteracted(targetTelemetryId ?? targetLink, slide);

      if (targetLink.startsWith('/') || targetLink.startsWith('?')) {
        const eventSpecificLink = targetLink.replace(/^\/event\//, `/${eventHandle ?? 'event'}/`);
        history.push(eventSpecificLink);
        return;
      }

      withWindow(global => {
        global.open(targetLink, '_blank');
      });
    },
    [valuePropLinkInteracted, eventHandle, history],
  );

  const goToPage = useCallback((target: number) => {
    // Cut instantly (no slide animation) when the change skips more than one slot.
    if (Math.abs(target - currentPageRef.current) > 1) {
      setSuppressTransition(true);
    }
    currentPageRef.current = target;
    setCurrentPage(target);
  }, []);

  // Keep the ref in sync for any page change and clear the one-step transition suppression once the
  // jumped positions are committed — they are already in place, so restoring the transition moves
  // nothing.
  useEffect(() => {
    currentPageRef.current = currentPage;
    setSuppressTransition(false);
  }, [currentPage]);

  useEffect(() => {
    if (currentPage >= content.length && content.length > 0) {
      goToPage(content.length - 1);
    }
  }, [currentPage, content.length, goToPage]);

  const nextPage = useCallback(() => {
    const current = currentPageRef.current;
    goToPage(current < content.length - 1 ? current + 1 : 0);
  }, [content.length, goToPage]);

  useEffect(() => {
    if (!autoRotatePeriodMs || content.length <= 1) return;
    if (prefersReducedMotion) return;
    if (pauseOnHover && hover) return;

    // Depending on currentPage resets the interval on every change, so a slide always shows for the
    // full period whether it was reached by auto-rotation or by a manual arrow/dot tap.
    const timer = setInterval(nextPage, autoRotatePeriodMs);
    return () => clearInterval(timer);
  }, [autoRotatePeriodMs, content.length, prefersReducedMotion, pauseOnHover, hover, nextPage, currentPage]);

  // Reduced-motion visitors get an instant cut on every change (auto-rotation is already off above).
  const disableTransition = suppressTransition || prefersReducedMotion;

  // Manual arrow navigation wraps around. Auto-rotation keeps running — it just resets to a full
  // interval on the slide the viewer lands on.
  const goToPrev = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      const current = currentPageRef.current;
      goToPage(current === 0 ? content.length - 1 : current - 1);
    },
    [content.length, goToPage],
  );

  const goToNext = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      const current = currentPageRef.current;
      goToPage(current === content.length - 1 ? 0 : current + 1);
    },
    [content.length, goToPage],
  );

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={event => {
        // Clear hover only when focus actually leaves the carousel, so a keyboard user tabbing out
        // resumes auto-rotation instead of staying paused forever.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHover(false);
        }
      }}
    >
      {content.map((asset, slideIndex) => {
        const slideLink = slideNav ? slideNav[slideIndex]?.link : link;
        const slideTelemetryId = slideNav ? slideNav[slideIndex]?.telemetryId : telemetryId;
        // Click-through when there is a link and no hover button (or always on mobile).
        const isClickable = !!slideLink && (!label || mobile);
        const commonProps = {
          key: slideIndex,
          index: slideIndex,
          currentPosition: slideIndex - currentPage,
          disableTransition,
          onClick: isClickable ? () => navigate(slideLink, slideTelemetryId, slideIndex) : undefined,
        };

        switch (asset.type) {
          case 'image':
            return (
              <CarouselImageContainer
                {...commonProps}
                asset={asset}
              />
            );
          case 'video':
            return (
              <CarouselVideoContainer
                {...commonProps}
                asset={asset}
              />
            );
        }
      })}

      <CarouselNavContainer
        link={link}
        label={label}
        showButton={hover}
      >
        {content.map((_, navIndex) => {
          return (
            <CarouselDot
              key={navIndex}
              index={navIndex}
              active={navIndex === currentPage}
              onSelect={page => {
                goToPage(page);
              }}
            />
          );
        })}
      </CarouselNavContainer>

      {content.length > 1 && (
        <>
          <CarouselArrow
            direction="prev"
            visible={mobile || hover}
            onClick={goToPrev}
          />
          <CarouselArrow
            direction="next"
            visible={mobile || hover}
            onClick={goToNext}
          />
        </>
      )}
    </div>
  );
};

const CarouselNavContainer = ({ children, link, label, showButton: shouldShowButton }: { children?: ReactNode[]; link?: string; label?: string; showButton?: boolean }) => {
  const dotCount = children?.filter(child => child !== null).length ?? 0;
  const [showButton, setShowButton] = useState(shouldShowButton);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (shouldShowButton) {
      setShowButton(true);
    }

    if (!shouldShowButton && showButton) {
      timeoutRef.current = setTimeout(() => {
        setShowButton(false);
      }, 400);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [shouldShowButton, showButton]);

  return (
    <Flex
      flexDirection="column"
      bottom="10px"
      width="100%"
      height="max-content"
      position="absolute"
      alignItems="center"
      justifyContent="flex-end"
      gap="10px"
      style={{ zIndex: 5 }}
    >
      {dotCount > 1 && (
        <Flex
          alignItems="center"
          justifyContent="center"
          gap="10px"
          width="100%"
          height="fit-content"
        >
          {children}
        </Flex>
      )}
      {link && label && showButton && (
        <CarouselButton
          shape="rounded"
          showing={shouldShowButton}
          onClick={() => {
            if (link && label) {
              withWindow(global => {
                global.location.href = link;
              });
            }
          }}
        >
          {label}
        </CarouselButton>
      )}
    </Flex>
  );
};

type CarouselDotProps = {
  active?: boolean;
  index: number;
  onSelect?: (index: number) => void;
};

const CarouselDot = (props: CarouselDotProps) => {
  const { active = false, index, onSelect } = props;
  const size = '10px';
  const dotStyle = {
    width: size,
    height: size,
    borderRadius: size,
    backgroundColor: active ? '#595959' : '#EBEBEB',
    opacity: 1,
    cursor: 'pointer',
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={dotStyle}
      onClick={() => {
        onSelect?.(index);
      }}
    ></div>
  );
};

export default ContentCarousel;
